/**
 * Packing List Section Component
 * 
 * Displays packing list items with weather- and city-context suggestions
 */

import { View, Pressable } from 'react-native';
import { useState } from 'react';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ThemedText, Body, Caption } from '@/components/ui/Text';
import { ChecklistItemRow } from './ChecklistItemRow';
import type { ChecklistItem } from '@/services/checklists';

type PackingListSectionProps = {
  items: ChecklistItem[];
  onToggleItem: (itemId: string) => void;
  onAddItem?: () => void;
  onEditItem?: (item: ChecklistItem) => void;
  loading?: boolean;
  weatherSummary?: {
    avgTemp: number;
    minTemp: number;
    maxTemp: number;
    hasPrecipitation: boolean;
    conditions: string[];
    isHistorical?: boolean;
  };
  weatherMessage?: string;
  weatherBasedSuggestions?: Array<{ title: string; notes?: string }>;
  onAddWeatherBasedSuggestions?: () => void;
  missingWeatherBasedSuggestionsCount?: number;
  cityContextPlaceLabel?: string;
  cityContextSuggestions?: Array<{ title: string; notes?: string }>;
  onAddCityContextSuggestions?: () => void;
  missingCityContextSuggestionsCount?: number;
};

export function PackingListSection({
  items,
  onToggleItem,
  onAddItem,
  onEditItem,
  loading = false,
  weatherSummary,
  weatherMessage,
  weatherBasedSuggestions = [],
  onAddWeatherBasedSuggestions,
  missingWeatherBasedSuggestionsCount = 0,
  cityContextPlaceLabel,
  cityContextSuggestions = [],
  onAddCityContextSuggestions,
  missingCityContextSuggestionsCount = 0,
}: PackingListSectionProps) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const sectionTitleStyle = { fontSize: 18, lineHeight: 22 } as const;

  const packingItems = items.filter(item => item.group === 'packing');
  const completedCount = packingItems.filter(item => item.completed).length;
  const totalCount = packingItems.length;
  const progress = totalCount > 0 ? completedCount / totalCount : 0;

  if (loading) {
    return (
      <Card variant="outlined">
        <View style={{ padding: theme.spacing(2) }}>
          <Body color={theme.colors.textSecondary}>Loading packing list...</Body>
        </View>
      </Card>
    );
  }

  if (totalCount === 0) {
    return (
      <Card variant="outlined">
        <View style={{ padding: theme.spacing(2), gap: theme.spacing(1) }}>
          <ThemedText variant="bodyStrong" style={sectionTitleStyle}>
            Packing List
          </ThemedText>
          <Body color={theme.colors.textSecondary}>
            No packing items yet. Items will be generated based on weather forecasts.
          </Body>
          {onAddItem && (
            <Button
              label="Add Item"
              variant="secondary"
              onPress={onAddItem}
              icon={<Ionicons name="add-outline" size={18} color={theme.colors.text} />}
            />
          )}
        </View>
      </Card>
    );
  }

  return (
    <Card variant="outlined" padding={theme.spacing(2)}>
      <View style={{ gap: theme.spacing(1.5) }}>
        <Pressable
          onPress={() => setExpanded(!expanded)}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <View style={{ flex: 1 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
              <View
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 8,
                  backgroundColor: theme.colors.primary + '15',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Ionicons
                  name="bag-outline"
                  size={16}
                  color={theme.colors.primary}
                />
              </View>
              <ThemedText variant="bodyStrong" style={sectionTitleStyle}>
                Packing List
              </ThemedText>
            </View>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: theme.spacing(1),
                marginTop: theme.spacing(0.5),
              }}
            >
              <Caption color={theme.colors.textSecondary}>
                {completedCount} of {totalCount} completed
              </Caption>
              <Caption color={theme.colors.textSecondary}>
                {Math.round(progress * 100)}%
              </Caption>
            </View>
          </View>
          <Ionicons
            name={expanded ? 'chevron-up' : 'chevron-down'}
            size={20}
            color={theme.colors.textSecondary}
          />
        </Pressable>

        {weatherSummary ? (
          <View
            style={{
              padding: theme.spacing(1.5),
              backgroundColor: weatherSummary.isHistorical 
                ? theme.colors.warning + '15' // Light warning background for historical
                : theme.colors.background,
              borderRadius: theme.radii.md,
              gap: theme.spacing(0.5),
              borderWidth: 1,
              borderColor: weatherSummary.isHistorical 
                ? theme.colors.warning + '40'
                : theme.colors.borderMuted,
            }}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
              <Ionicons 
                name={weatherSummary.isHistorical ? "time-outline" : "thermometer-outline"} 
                size={16} 
                color={weatherSummary.isHistorical ? theme.colors.warning : theme.colors.textSecondary} 
              />
              <Caption 
                color={weatherSummary.isHistorical ? theme.colors.warning : theme.colors.textSecondary} 
                style={{ fontSize: 12, flex: 1 }}
              >
                {weatherSummary.isHistorical ? '📊 Historical Average: ' : '🌤️ Forecast: '}
                {weatherSummary.minTemp}°C - {weatherSummary.maxTemp}°C
                {weatherSummary.hasPrecipitation && ' • Rain expected'}
              </Caption>
            </View>
            {weatherSummary.isHistorical && (
              <Caption color={theme.colors.textSecondary} style={{ fontSize: 11, marginLeft: 20 }}>
                Based on historical averages. Will update with actual forecast as trip approaches.
              </Caption>
            )}
          </View>
        ) : weatherMessage ? (
          <View
            style={{
              padding: theme.spacing(1.25),
              backgroundColor: theme.colors.background,
              borderRadius: theme.radii.md,
              gap: theme.spacing(0.5),
              borderWidth: 1,
              borderColor: theme.colors.borderMuted,
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <Ionicons 
              name="information-circle-outline" 
              size={16} 
              color={theme.colors.textSecondary} 
              style={{ marginRight: theme.spacing(0.5) }}
            />
            <Caption 
              color={theme.colors.textSecondary} 
              style={{ fontSize: 12, flex: 1 }}
            >
              {weatherMessage}
            </Caption>
          </View>
        ) : null}

        {/* Weather-based suggestions (actionable) */}
        {expanded &&
          weatherBasedSuggestions.length > 0 &&
          missingWeatherBasedSuggestionsCount > 0 &&
          onAddWeatherBasedSuggestions && (
            <Card
              variant="outlined"
              style={{
                padding: theme.spacing(1.5),
                borderRadius: theme.radii.md,
                backgroundColor: theme.colors.primary + '08',
                borderColor: theme.colors.borderMuted,
              }}
            >
              <View style={{ gap: theme.spacing(1) }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75) }}>
                  <Ionicons name="sparkles-outline" size={18} color={theme.colors.primary} />
                  <ThemedText variant="bodyStrong">Weather-based packing</ThemedText>
                </View>

                <Caption color={theme.colors.textSecondary}>
                  Suggested adds based on {weatherSummary?.isHistorical ? 'historical averages' : 'the forecast'}.
                </Caption>

                <View style={{ gap: theme.spacing(0.5) }}>
                  {weatherBasedSuggestions.slice(0, 4).map((s) => (
                    <View key={s.title} style={{ flexDirection: 'row', alignItems: 'flex-start', gap: theme.spacing(0.75) }}>
                      <Ionicons name="checkmark-circle-outline" size={16} color={theme.colors.primary} />
                      <View style={{ flex: 1 }}>
                        <ThemedText variant="bodyStrong" style={{ fontSize: 13 }}>
                          {s.title}
                        </ThemedText>
                        {s.notes ? (
                          <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
                            {s.notes}
                          </Caption>
                        ) : null}
                      </View>
                    </View>
                  ))}
                </View>

                <Button
                  label={`Add suggested items (${missingWeatherBasedSuggestionsCount})`}
                  variant="secondary"
                  size="small"
                  onPress={onAddWeatherBasedSuggestions}
                  icon={<Ionicons name="add-outline" size={18} color={theme.colors.text} />}
                  accessibilityLabel="Add weather-based packing suggestions"
                  accessibilityHint="Adds suggested packing items to your checklist"
                />
              </View>
            </Card>
          )}

        {expanded &&
          cityContextSuggestions.length > 0 &&
          missingCityContextSuggestionsCount > 0 &&
          onAddCityContextSuggestions && (
            <Card
              variant="outlined"
              style={{
                padding: theme.spacing(1.5),
                borderRadius: theme.radii.md,
                backgroundColor: theme.colors.primary + '08',
                borderColor: theme.colors.borderMuted,
              }}
            >
              <View style={{ gap: theme.spacing(1) }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75) }}>
                  <Ionicons name="location-outline" size={18} color={theme.colors.primary} />
                  <ThemedText variant="bodyStrong">City-specific recommendations</ThemedText>
                </View>

                <Caption color={theme.colors.textSecondary}>
                  {cityContextPlaceLabel
                    ? `Suggested adds based on local context in ${cityContextPlaceLabel}.`
                    : 'Suggested adds based on your destination.'}
                </Caption>

                <View style={{ gap: theme.spacing(0.5) }}>
                  {cityContextSuggestions.slice(0, 6).map((s) => (
                    <View
                      key={s.title}
                      style={{ flexDirection: 'row', alignItems: 'flex-start', gap: theme.spacing(0.75) }}
                    >
                      <Ionicons name="checkmark-circle-outline" size={16} color={theme.colors.primary} />
                      <View style={{ flex: 1 }}>
                        <ThemedText variant="bodyStrong" style={{ fontSize: 13 }}>
                          {s.title}
                        </ThemedText>
                        {s.notes ? (
                          <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
                            {s.notes}
                          </Caption>
                        ) : null}
                      </View>
                    </View>
                  ))}
                </View>

                <Button
                  label={`Add suggested items (${missingCityContextSuggestionsCount})`}
                  variant="secondary"
                  size="small"
                  onPress={onAddCityContextSuggestions}
                  icon={<Ionicons name="add-outline" size={18} color={theme.colors.text} />}
                  accessibilityLabel="Add city-specific packing recommendations"
                  accessibilityHint="Adds suggested destination-aware items to your checklist"
                />
              </View>
            </Card>
          )}

        {expanded && (
          <>
            <View
              style={{
                height: 3,
                backgroundColor: theme.colors.borderMuted,
                borderRadius: theme.radii.sm,
                overflow: 'hidden',
              }}
            >
              <View
                style={{
                  height: '100%',
                  width: `${progress * 100}%`,
                  backgroundColor: theme.colors.primary,
                }}
              />
            </View>

            <View style={{ gap: theme.spacing(1.5) }}>
              {packingItems.map((item) => (
                <ChecklistItemRow
                  key={item.id}
                  item={item}
                  onToggle={onToggleItem}
                  onPress={onEditItem}
                />
              ))}
            </View>

            {onAddItem && (
              <Button
                label="Add Custom Item"
                variant="secondary"
                onPress={onAddItem}
                icon={<Ionicons name="add-outline" size={18} color={theme.colors.text} />}
              />
            )}
          </>
        )}
      </View>
    </Card>
  );
}

